import { config } from '../../config';
import api from '../../services/api';
import { CheckoutInitRequest } from '../../types/requests/CheckoutInitRequest';
import SendPaylinkRequest from '../../types/requests/sendPaylinkRequest';
import { UpdateOrderReferenceRequest } from '../../types/requests/updateOrderReferenceRequest';
import { CheckoutDetailsResponseData } from '../../types/response/CheckoutDetailsResponseData';
import CheckoutInitResponse from '../../types/response/CheckoutInitResponse';
import { PostResponseData } from '../../types/response/PostResponseData';
import ResponseError from '../../types/response/ResponseError';
import { AppThunkAction, AppThunkDispatch } from '../thunk';
import {
    initCheckoutStarted,
    initCheckoutSucceeded,
    initCheckoutFailed,
    sendPaylinkStarted,
    sendPaylinkSucceeded,
    sendPaylinkFailed,
    getPaylinkDetailsStarted,
    getPaylinkDetailsSucceeded,
    getPaylinkDetailsFailed,
    clearPaylinkDetails,
    abandonPaylinkStarted,
    abandonPaylinkSucceeded,
    abandonPaylinkFailed,
    initPurchaseSucceeded,
    updateOrderReferenceStarted,
    updateOrderReferenceSucceeded,
    updateOrderReferenceFailed,
} from './checkoutSlice';

const initCheckoutSession =
    (checkoutData: CheckoutInitRequest): AppThunkAction =>
    async (dispatch: AppThunkDispatch) => {
        dispatch(initCheckoutStarted());

        try {
            const response = await api.postWithResponseType<
                CheckoutInitRequest,
                CheckoutInitResponse
            >('/checkouts', checkoutData, config.WalleyApiHost);

            dispatch(initCheckoutSucceeded(response));
        } catch (error) {
            dispatch(initCheckoutFailed(error as ResponseError));
        }
    };

const initPurchaseSession =
    (checkoutData: CheckoutInitRequest): AppThunkAction =>
    async (dispatch: AppThunkDispatch) => {
        dispatch(initCheckoutStarted());

        try {
            const response = await api.postWithResponseType<CheckoutInitRequest, PostResponseData>(
                '/purchases',
                checkoutData
            );

            dispatch(initPurchaseSucceeded(response));
        } catch (error) {
            dispatch(initCheckoutFailed(error as ResponseError));
        }
    };

const sendPaylink =
    (privatePaylinkId: string, mobilePhoneNumber: string): AppThunkAction =>
    async (dispatch: AppThunkDispatch) => {
        dispatch(sendPaylinkStarted());

        try {
            await api.post<SendPaylinkRequest>(
                `/checkouts/${privatePaylinkId}/paylink`,
                {
                    destination: {
                        mobilePhoneNumber,
                    },
                },
                config.WalleyApiHost
            );

            dispatch(sendPaylinkSucceeded());
        } catch (error) {
            dispatch(sendPaylinkFailed(error as ResponseError));
        }
    };

const updateOrderReference =
    (privatePaylinkId: string, reference: string): AppThunkAction =>
    async (dispatch: AppThunkDispatch) => {
        dispatch(updateOrderReferenceStarted());

        try {
            await api.put<UpdateOrderReferenceRequest>(
                `/checkouts/${privatePaylinkId}/reference`,
                {
                    Reference: reference,
                },
                config.WalleyApiHost
            );

            dispatch(updateOrderReferenceSucceeded(reference));
        } catch (error) {
            dispatch(updateOrderReferenceFailed(error as ResponseError));
        }
    };

const abandonPaylink =
    (privatePaylinkId: string): AppThunkAction =>
    async (dispatch: AppThunkDispatch) => {
        dispatch(abandonPaylinkStarted());

        try {
            await api.del(`/checkouts/${privatePaylinkId}`, config.WalleyApiHost);

            dispatch(abandonPaylinkSucceeded());
        } catch (error) {
            dispatch(abandonPaylinkFailed(error as ResponseError));
        }
    };

const getPaylinkDetails =
    (id: string): AppThunkAction =>
    async (dispatch: AppThunkDispatch) => {
        dispatch(clearPaylinkDetails());
        dispatch(getPaylinkDetailsStarted());

        try {
            const response = await api.get<CheckoutDetailsResponseData>(
                `/checkouts/${id}`,
                undefined,
                undefined,
                6
            );

            dispatch(getPaylinkDetailsSucceeded(response));
        } catch (error) {
            dispatch(getPaylinkDetailsFailed(error as ResponseError));
        }
    };

export default {
    initCheckoutSession,
    sendPaylink,
    getPaylinkDetails,
    abandonPaylink,
    initPurchaseSession,
    updateOrderReference,
};
